import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ConfirmDialog,
  IconButton,
  PageHeader,
  Select,
  type SelectOption,
} from '../../components';
import { apiErrorMessage, apiRequest } from '../../lib/api';
import { usePermissions } from '../../lib/permissions';
import type { ListResponse } from '../../lib/useCrud';
import type { UserOption } from '../leads/types';
import { FollowupFormModal } from './FollowupFormModal';
import type { FollowupCreatePayload, FollowupLink, FollowupRow } from './types';

/** Fields the PATCH endpoint accepts — the linked entity is intentionally immutable. */
type FollowupUpdatePayload = Omit<FollowupCreatePayload, 'lead_id' | 'client_id' | 'contact_id'>;

type LookupResponse<T> = ListResponse<T>;

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ENTITY_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All linked types' },
  { value: 'lead', label: 'Leads' },
  { value: 'client', label: 'Clients' },
  { value: 'contact', label: 'Contacts' },
];

type View = 'all' | 'overdue' | 'today';

const VIEW_TABS: Array<{ value: View; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
];

const PAGE_SIZE = 25;

function todayWindow(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

function isOverdue(f: FollowupRow): boolean {
  return (
    !!f.due_date &&
    (f.status === 'pending' || f.status === 'in_progress') &&
    new Date(f.due_date).getTime() < Date.now()
  );
}

function formatDue(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function LinkedCell({ link }: { link: FollowupLink | null }) {
  if (!link) return <span className="muted">—</span>;
  const label = link.label ?? 'Unnamed';
  const badge = (
    <span className="badge gray" style={{ marginRight: 6, textTransform: 'capitalize' }}>
      {link.type}
    </span>
  );
  // Leads, clients and contacts all have detail routes.
  return (
    <span>
      {badge}
      <Link to={`/${link.type}s/${link.id}`}>{label}</Link>
    </span>
  );
}

export function FollowupsPage() {
  const qc = useQueryClient();
  // Read-only users can't create/edit/complete; deletes are admin-only (the
  // backend enforces both — this just keeps the UI honest).
  const { canWrite, isAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = (searchParams.get('view') as View) || 'all';
  const setView = (next: View) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('view');
        else p.set('view', next);
        return p;
      },
      { replace: true },
    );
    setOffset(0);
  };

  const [status, setStatus] = useState('');
  const [entityType, setEntityType] = useState('');
  const [assignee, setAssignee] = useState('');
  const [offset, setOffset] = useState(0);

  const [editing, setEditing] = useState<FollowupRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<FollowupRow | null>(null);

  const listQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    if (status) params.set('status', status);
    if (entityType) params.set('entity_type', entityType);
    if (assignee) params.set('assigned_user_id', assignee);
    if (view === 'overdue') params.set('overdue', 'true');
    if (view === 'today') {
      const { from, to } = todayWindow();
      params.set('due_from', from);
      params.set('due_to', to);
    }
    return params.toString();
  }, [status, entityType, assignee, view, offset]);

  const followups = useQuery({
    queryKey: ['followups', listQuery],
    queryFn: () => apiRequest<ListResponse<FollowupRow>>(`/followups?${listQuery}`),
  });

  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<LookupResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });
  const users = usersQ.data?.data ?? [];
  const userFilterOpts: SelectOption[] = [
    { value: '', label: 'Any assignee' },
    ...users.map((u) => ({ value: u.id, label: u.full_name })),
  ];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['followups'] });

  const createMut = useMutation({
    mutationFn: (body: FollowupCreatePayload) =>
      apiRequest<{ data: FollowupRow }>('/followups', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: FollowupUpdatePayload }) =>
      apiRequest<{ data: FollowupRow }>(`/followups/${args.id}`, {
        method: 'PATCH',
        body: args.body,
      }),
    onSuccess: invalidate,
  });
  const completeMut = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: FollowupRow }>(`/followups/${id}/complete`, { method: 'POST' }),
    onSuccess: invalidate,
    onError: (err) => alert(apiErrorMessage(err, 'Could not mark complete')),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/followups/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const rows = followups.data?.data ?? [];
  const total = followups.data?.pagination?.total ?? 0;
  const currentPageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Follow-ups"
        subtitle="Your team's follow-up work queue across leads, clients and contacts."
        actions={
          canWrite ? (
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              New follow-up
            </Button>
          ) : null
        }
      />

      <div className="card" style={{ marginBottom: 12 }}>
        <div
          className="card-header"
          style={{ gap: 8, borderBottom: '1px solid var(--border)' }}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            {VIEW_TABS.map((t) => (
              <Button
                key={t.value}
                size="sm"
                variant={view === t.value ? 'primary' : 'secondary'}
                onClick={() => setView(t.value)}
              >
                {t.label}
              </Button>
            ))}
          </div>
        </div>
        <div
          className="card-body"
          style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(3, 1fr)' }}
        >
          <Select
            placeholder="All statuses"
            value={status}
            options={STATUS_FILTER_OPTIONS}
            onChange={(v) => {
              setStatus(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="All linked types"
            value={entityType}
            options={ENTITY_FILTER_OPTIONS}
            onChange={(v) => {
              setEntityType(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="Any assignee"
            value={assignee}
            options={userFilterOpts}
            onChange={(v) => {
              setAssignee(v);
              setOffset(0);
            }}
          />
        </div>
      </div>

      {followups.isLoading && <div className="card card-body muted">Loading…</div>}
      {followups.error && (
        <div className="alert">
          {followups.error instanceof Error
            ? followups.error.message
            : 'Failed to load follow-ups'}
        </div>
      )}
      {followups.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Due</th>
                <th>Status</th>
                <th>Type</th>
                <th>Description</th>
                <th>Assignee</th>
                <th>Linked to</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No follow-ups match this filter.
                  </td>
                </tr>
              )}
              {rows.map((f) => {
                const overdue = isOverdue(f);
                return (
                  <tr key={f.id}>
                    <td className={overdue ? 'alert' : ''} style={overdue ? { padding: 8 } : undefined}>
                      {formatDue(f.due_date)}
                    </td>
                    <td>
                      <span
                        className={`badge ${f.status === 'done' ? 'green' : 'gray'}`}
                        style={{ textTransform: 'capitalize' }}
                      >
                        {f.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{f.followup_type ?? <span className="muted">—</span>}</td>
                    <td>{f.description ?? <span className="muted">—</span>}</td>
                    <td>{f.assignee?.full_name ?? <span className="muted">—</span>}</td>
                    <td>
                      <LinkedCell link={f.link} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {canWrite && f.status !== 'done' && f.status !== 'cancelled' && (
                          <IconButton
                            name="check"
                            label="Mark complete"
                            onClick={() => completeMut.mutate(f.id)}
                          />
                        )}
                        {canWrite && (
                          <IconButton name="edit" label="Edit" onClick={() => setEditing(f)} />
                        )}
                        {isAdmin && (
                          <IconButton
                            name="trash"
                            label="Delete"
                            onClick={() => setDeleting(f)}
                          />
                        )}
                        {!canWrite && (
                          <span className="muted" style={{ fontSize: 12 }}>
                            View only
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            className="card-header"
            style={{
              justifyContent: 'space-between',
              borderTop: '1px solid var(--border)',
              borderBottom: 'none',
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              {total === 0 ? '0' : `${offset + 1}–${currentPageEnd}`} of {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                disabled={currentPageEnd >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <FollowupFormModal
          row={editing === 'new' ? undefined : editing}
          users={users}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            if (editing === 'new') {
              await createMut.mutateAsync(payload);
            } else {
              // Drop the linked-entity keys: PATCH only touches editable fields.
              const { lead_id: _l, client_id: _c, contact_id: _ct, ...update } = payload;
              await updateMut.mutateAsync({ id: editing.id, body: update });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete follow-up"
        message={deleting ? <>Delete this follow-up? This cannot be undone.</> : null}
        destructive
        confirmLabel="Delete"
        loading={deleteMut.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteMut.mutateAsync(deleting.id);
            setDeleting(null);
          } catch (err) {
            alert(apiErrorMessage(err, 'Delete failed'));
          }
        }}
      />
    </>
  );
}
